import { Favorite, FavoriteBorder } from "@mui/icons-material";
import { Checkbox } from "@mui/material";
import { format } from "date-fns";
import {
  ChangeEvent,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import Dropdown from "../../components/dropdown/dropdown";
import MediaTypeIcon from "../../components/media-format/media-type-icon";
import { Asset, Category } from "../../generated/gql/graphql";
import useCollection from "../../hooks/mutation/collection/use-collection";
import { AssetListContext } from "../../providers/asset-list-provider";
import { ConfirmationModalType } from "../../types/confirm-modal";
import { findRootCategoryById } from "../../utils";
import { getLatestDate } from "../../utils/date";

type Props = {
  asset: Asset;
  categories: Category[];
  collectionId?: string;
  rawCollectionId?: string;
  setConfirm: (val: ConfirmationModalType) => void;
};

export default function CollectionAssetListItem(props: Props): ReactElement {
  const { asset, categories, collectionId, rawCollectionId, setConfirm } =
    props;

  const { selectedFiles, setSelectedFile } = useContext(AssetListContext);
  const [selected, setSelected] = useState(selectedFiles.includes(asset));

  const { updateCollectionThumbnail } = useCollection();

  useEffect(() => {
    setSelected(selectedFiles.includes(asset));
  }, [selectedFiles]);

  const navigate = useNavigate();

  async function onFavorite(favorite: boolean): Promise<void> {
    if (!rawCollectionId || !asset.id) {
      return;
    }

    try {
      await updateCollectionThumbnail({
        assetId: asset.id,
        collectionId: rawCollectionId,
      });
    } catch (error) {
      console.error(error);
    }
  }

  function onClick(): void {
    if (!asset.id) {
      return;
    }

    navigate(`/file/${asset.id}/1`);
  }

  function onSelect(): void {
    setSelectedFile(asset, !selected);
    setSelected(!selected);
  }

  function capitalizeFirstLetter(string: string): string {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  return (
    <div
      style={{ borderColor: "#E8E8E8" }}
      className="flex shadow-none rounded-[15px] border-solid border-1 w-full p-[10px] text-[14px] items-center mb-20"
    >
      <div className="flex-1">
        <div className="flex items-center">
          <div className="flex w-[40px] flex-col">
            <Checkbox
              key={asset.id}
              id={asset.id || ""}
              onChange={onSelect}
              value={asset.id}
              checked={selected}
              sx={{ "& .MuiSvgIcon-root": { fontSize: 22 } }}
            />
            <Checkbox
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                onFavorite(!event.target.checked)
              }
              defaultChecked={asset.favorite || false}
              icon={<FavoriteBorder />}
              checkedIcon={<Favorite />}
            />
          </div>
          <div className="cursor-pointer" onClick={onClick}>
            <MediaTypeIcon
              asset={asset}
              classes="text-[50px] max-w-[50px] max-h-[50px] ml-20 object-contain"
            />
          </div>
        </div>
      </div>
      <div onClick={onClick} className="flex-1 pr-10 cursor-pointer">
        {asset.name}
      </div>
      <div onClick={onClick} className="flex-1 cursor-pointer">
        {findRootCategoryById(asset.categoryId || "", categories)}
      </div>
      <div onClick={onClick} className="flex-1 cursor-pointer">
        {capitalizeFirstLetter((asset?.assetTypes || "").replace("_", " "))}
      </div>
      <div className="flex-1">
        <div className="flex justify-between items-center">
          {asset &&
            asset.dateCreated &&
            format(getLatestDate(asset), "dd-MM-yyyy")}
          <Dropdown
            onConfirm={setConfirm}
            asset={asset}
            setCollectionItem
            collectionId={collectionId ?? undefined}
          />
        </div>
      </div>
    </div>
  );
}
